import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';

/**
 * Google OAuth Callback Handler
 * Handles the callback from Google OAuth after user authenticates.
 * Exchanges the authorization code for tokens via our backend.
 * Supports subdomain redirect - returns user to original subdomain after auth.
 */
const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in React StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Check for error from Google
        const error = searchParams.get('error');
        if (error) {
          console.error('Google OAuth error:', error);
          toast.error(`Google login failed: ${error}`);
          navigate('/login', { replace: true });
          return;
        }

        // Get the authorization code and state from URL
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        
        if (!code) {
          console.error('No authorization code found in URL');
          toast.error('Authentication failed - no authorization code');
          navigate('/login', { replace: true });
          return;
        }

        console.log('Processing Google OAuth callback with authorization code');

        // Exchange the code for tokens via our backend
        // Include the state parameter so backend can parse the origin
        const response = await api.get('/auth/google/callback', {
          params: { code, state }
        });

        const { access_token, user, origin_url } = response.data;

        if (!access_token || !user) {
          throw new Error('Invalid response from server');
        }

        // Store token and user data
        localStorage.setItem('token', access_token);
        loginWithToken(access_token, user);

        // Show success message
        toast.success(`Welcome${user.first_name ? `, ${user.first_name}` : ''}!`);

        // Determine the redirect path based on user role and setup status
        let redirectPath;
        if (['admin', 'main_admin', 'company_admin'].includes(user.role)) {
          if (!user.has_completed_setup) {
            redirectPath = '/admin/company/setup';
          } else {
            redirectPath = '/admin';
          }
        } else {
          redirectPath = '/admin/dashboard';
        }

        // Check if we need to redirect to a different origin (subdomain support)
        // If origin_url is different from current origin, redirect with token
        if (origin_url && origin_url !== window.location.origin) {
          console.log(`Redirecting to origin: ${origin_url}`);
          // Redirect to the original subdomain with token in URL (will be handled by that domain)
          const redirectUrl = new URL(redirectPath, origin_url);
          redirectUrl.searchParams.set('token', access_token);
          redirectUrl.searchParams.set('oauth_redirect', 'true');
          window.location.href = redirectUrl.toString();
        } else {
          // Same origin, just navigate
          navigate(redirectPath, { replace: true });
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        const errorMessage = error.response?.data?.detail || 'Authentication failed. Please try again.';
        toast.error(errorMessage);
        navigate('/login', { replace: true });
      }
    };

    processCallback();
  }, [navigate, loginWithToken, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Completing Google sign in...</p>
        <p className="text-gray-400 text-sm mt-2">Please wait while we verify your credentials</p>
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
